import React from 'react';
import { etendreOptionsParCible } from '../Prestations/prestationsTypes';
import { ArrowRight, Loader2, Repeat, CheckCircle2, AlertCircle } from 'lucide-react';
import { moduleApi } from '../../lib/api';

interface FormulaItem { catalog_item_id: string; label: string; required: boolean; }
interface CategoryThreshold { category_id: string; category_name: string; minimum_quantity: number; }
interface FormulaSubscription {
    id: string; formula_id: string; formula_name: string;
    occurrences_used: number; occurrences_total: number; status: string;
    passages_libres?: boolean;
    formula_items: FormulaItem[];
    category_thresholds: CategoryThreshold[];
    category_thresholds_mode: 'any' | 'all';
    threshold_kind?: 'quantite' | 'montant';
    minimum_amount_ttc?: number;
}
interface CatalogItem { id: string; label: string; category_id?: string | null; unit_price_ttc?: number; }
// quantities[occurrence_number][catalog_item_id] = quantité choisie pour ce passage (0 = pas inclus).
type QuantitiesByPassage = Record<number, Record<string, number>>;
export interface DevisItemDraft {
    catalog_item_id: string; quantity: number; manual_discount_percent: number; offered_quantity: number;
    passage_number?: number; passage_label?: string;
    // Ligne libre (ex. frais kilométriques repris de la demande du simulateur).
    label?: string; unit_price_ttc?: number; tax_rate?: number;
}

/**
 * Étape « Planning des passages » du pipeline, affichée avant la composition du devis quand le contact est
 * Professionnel et a une formule active (demande d'Emeni, 2026-09-03) : pour chacun des passages prévus
 * dans l'année par la formule, on précise avec le client une période approximative et la QUANTITÉ de
 * chaque prestation attendue. « Suivant » génère une ligne de devis PAR PASSAGE (jamais agrégées entre
 * elles — chaque passage doit rester visible séparément sur le devis, cf. DevisPrestationComposer.tsx qui
 * groupe l'affichage par passage_number) et ouvre le composeur déjà prérempli. La réduction/gratuité de
 * formule (déjà configurée par prestation dans Prestations > Formules) s'applique ensuite automatiquement
 * côté serveur, passage par passage — rien à ressaisir ici.
 *
 * Sans formule active (ou contact Particulier — les formules ne leur sont jamais proposées), cette étape
 * est invisible : `onSkip` bascule directement sur le composeur classique.
 */
export function FormulaPassagesPlanner({
    dossierId, contactId, contactType, selectedFormulaId, onDone, onSkip,
}: {
    dossierId: string; contactId: string | null; contactType: string | null;
    // Formule retenue par le client dans sa demande de devis (dossier.selected_formula_id), AVANT toute
    // souscription/contrat (2026-09-26, signale par Emeni : un devis "ESSENTIEL, 75 sieges" facture sur un
    // seul passage — cette etape etait entierement sautee faute de souscription existante pour un tout
    // nouveau client, alors que la formule choisie est deja connue et suffit a planifier les passages).
    selectedFormulaId?: string | null;
    onDone: (args: { items: DevisItemDraft[]; formulaSubscriptionId: string }) => void;
    onSkip: () => void;
}) {
    const [loading, setLoading] = React.useState(true);
    const [subscriptions, setSubscriptions] = React.useState<FormulaSubscription[]>([]);
    const [catalog, setCatalog] = React.useState<CatalogItem[]>([]);
    const [selectedSubId, setSelectedSubId] = React.useState<string>('');
    const [periodes, setPeriodes] = React.useState<Record<number, string>>({});
    const [quantities, setQuantities] = React.useState<QuantitiesByPassage>({});
    const [saving, setSaving] = React.useState(false);
    const [erreur, setErreur] = React.useState('');

    React.useEffect(() => {
        moduleApi.getBillingSettings().then((r: any) => {
            try { setCatalog(etendreOptionsParCible(JSON.parse(r?.prestations_catalog_devis || r?.prestations_catalog || '[]'))); } catch { setCatalog([]); }
        }).catch(() => setCatalog([]));
        if (!contactId || contactType !== 'PROFESSIONNEL') { setLoading(false); return; }
        Promise.all([
            moduleApi.getContactFormulaSubscriptions(contactId).catch(() => []),
            // Sans souscription (client pas encore signe), on retombe sur la definition de LA formule
            // choisie : une souscription "virtuelle" (id vide) suffit a piloter le meme formulaire de
            // planning par passage, avec le vrai nombre de passages de la formule.
            // Dossier ouvert à la main (aucune formule choisie au préalable) : on charge quand même les formules,
            // pour que l'établissement puisse en choisir une et planifier ses passages (sinon le devis ne chiffre
            // qu'un seul passage alors que la formule en comprend plusieurs).
            moduleApi.getPrestationFormulas().catch(() => []),
        ]).then(([subsRaw, formulasRaw]: [any, any]) => {
            const subs = (Array.isArray(subsRaw) ? subsRaw : []).filter((s: any) => s.status === 'active');
            const dejaCouverte = subs.some((s: any) => s.formula_id === selectedFormulaId);
            const aProposer = (Array.isArray(formulasRaw) ? formulasRaw : []).filter((x: any) => selectedFormulaId
                ? x.id === selectedFormulaId
                : (x.is_active === undefined || !!Number(x.is_active)) && ['TOUS', 'PROFESSIONNEL'].includes(x.client_type_scope || 'TOUS')
                    && !subs.some((sx: any) => sx.formula_id === x.id));
            if (!dejaCouverte) {
                for (const f of aProposer) {
                    subs.push({
                        id: '', formula_id: f.id, formula_name: f.name,
                        occurrences_used: 0, occurrences_total: Math.max(1, Number(f.occurrences_total) || 1),
                        passages_libres: !!Number(f.passages_libres),
                        status: 'virtuelle',
                        formula_items: f.items || [],
                        category_thresholds: f.category_thresholds || [],
                        category_thresholds_mode: f.category_thresholds_mode === 'all' ? 'all' : 'any',
                        threshold_kind: f.threshold_kind === 'montant' ? 'montant' : 'quantite',
                        minimum_amount_ttc: f.minimum_amount_ttc || 0,
                    });
                }
            }
            setSubscriptions(subs);
            // Une seule formule en jeu (le cas courant : la formule que le client vient de demander) —
            // autant la présélectionner plutôt que de forcer un clic supplémentaire dans la liste.
            if (subs.length === 1) setSelectedSubId(subs[0].id || subs[0].formula_id);
        }).finally(() => setLoading(false));
    }, [contactId, contactType, selectedFormulaId]);

    const subscription = subscriptions.find((s) => (s.id || s.formula_id) === selectedSubId) || null;
    // Souscription virtuelle (pas encore de contrat signé) : le planning s'enregistre par formula_id, la
    // vraie souscription n'existant pas encore — cf. PATCH /prestation-dossiers/:id/formula-passages qui
    // accepte déjà les deux (formula_subscription_id OU formula_id).
    const estVirtuelle = !!subscription && !subscription.id;
    const catalogById = React.useMemo(() => Object.fromEntries(catalog.map((c) => [c.id, c])), [catalog]);

    React.useEffect(() => {
        if (!subscription) { setPeriodes({}); setQuantities({}); return; }
        // Passages libres : on démarre avec un seul passage, l'établissement en ajoute autant que nécessaire.
        const n = subscription.passages_libres ? 1 : Math.max(1, subscription.occurrences_total);
        const nextPeriodes: Record<number, string> = {};
        const nextQuantities: QuantitiesByPassage = {};
        for (let i = 1; i <= n; i++) {
            nextPeriodes[i] = '';
            nextQuantities[i] = Object.fromEntries(subscription.formula_items.filter((it) => it.required).map((it) => [it.catalog_item_id, 1]));
        }
        setPeriodes(nextPeriodes);
        setQuantities(nextQuantities);
    }, [subscription?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const changerQuantite = (occurrenceNumber: number, catalogItemId: string, quantite: number) =>
        setQuantities((prev) => ({ ...prev, [occurrenceNumber]: { ...prev[occurrenceNumber], [catalogItemId]: Math.max(0, Math.round(quantite) || 0) } }));

    // Total PAR CATÉGORIE, PAR PASSAGE — le seuil ne se cumule pas sur l'année, chaque passage doit
    // l'atteindre individuellement (précision d'Emeni, 2026-09-03), même règle que côté serveur (voir
    // computeDevisTotals). Affiché en direct dans la carte de CE passage pendant la saisie.
    const totauxParCategoriePourPassage = (occurrenceNumber: number) => {
        const totaux: Record<string, number> = {};
        for (const [catalogItemId, qte] of Object.entries(quantities[occurrenceNumber] || {})) {
            const categoryId = catalogById[catalogItemId]?.category_id;
            if (!categoryId || !qte) continue;
            totaux[categoryId] = (totaux[categoryId] || 0) + qte;
        }
        return totaux;
    };

    const libres = !!Number(subscription?.passages_libres);
    const ajouterPassage = () => {
        const suivant = Math.max(0, ...Object.keys(periodes).map(Number)) + 1;
        setPeriodes((prev) => ({ ...prev, [suivant]: '' }));
        setQuantities((prev) => ({ ...prev, [suivant]: Object.fromEntries((subscription?.formula_items || []).filter((it) => it.required).map((it) => [it.catalog_item_id, 1])) }));
    };
    const retirerPassage = (num: number) => {
        setPeriodes((prev) => { const { [num]: _a, ...reste } = prev; return reste; });
        setQuantities((prev) => { const { [num]: _b, ...reste } = prev; return reste; });
    };
    // Passages libres : le minimum est ANNUEL, mesuré sur tous les passages ensemble (une seule jauge).
    const brutAnnuel = Math.round(Object.values(quantities).reduce(
        (total, parPassage) => total + Object.entries(parPassage || {}).reduce(
            (s, [catalogItemId, qte]) => s + (Number(qte) || 0) * (Number(catalogById[catalogItemId]?.unit_price_ttc) || 0), 0), 0) * 100) / 100;
    const seuilAnnuel = libres && subscription?.threshold_kind === 'montant' ? Math.max(0, Number(subscription?.minimum_amount_ttc) || 0) : 0;

    const renderSeuils = (occurrenceNumber: number) => {
        if (!subscription) return null;
        if (libres) return null;
        // Seuil en montant (2026-09-08) : brut TTC du passage avant remise, à atteindre passage par passage.
        const seuilMontant = subscription.threshold_kind === 'montant'
            ? Math.max(0, Number(subscription.minimum_amount_ttc) || 0)
            : 0;
        if (seuilMontant > 0) {
            const brut = Math.round(Object.entries(quantities[occurrenceNumber] || {}).reduce(
                (s, [catalogItemId, qte]) => s + (Number(qte) || 0) * (Number(catalogById[catalogItemId]?.unit_price_ttc) || 0), 0) * 100) / 100;
            const ok = brut >= seuilMontant;
            const fr = (n: number) => n.toFixed(2).replace('.', ',');
            return (
                <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1.5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">
                        Seuil d'éligibilité de ce passage
                    </p>
                    <p className={`text-xs font-bold flex items-center gap-1.5 ${ok ? 'text-green-500' : 'text-amber-500'}`}>
                        {ok ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />} {fr(brut)} € / {fr(seuilMontant)} € TTC
                    </p>
                </div>
            );
        }
        if (subscription.category_thresholds.length === 0) return null;
        const totaux = totauxParCategoriePourPassage(occurrenceNumber);
        return (
            <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1.5">
                <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">
                    Seuil d'éligibilité de ce passage ({subscription.category_thresholds_mode === 'all' ? 'tous requis' : 'un seul suffit'})
                </p>
                {subscription.category_thresholds.map((t) => {
                    const atteint = t.category_id in totaux ? totaux[t.category_id] : 0;
                    const ok = atteint >= t.minimum_quantity;
                    return (
                        <p key={t.category_id} className={`text-xs font-bold flex items-center gap-1.5 ${ok ? 'text-green-500' : 'text-amber-500'}`}>
                            {ok ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />} {t.category_name} : {atteint}/{t.minimum_quantity}
                        </p>
                    );
                })}
            </div>
        );
    };

    const suivant = async () => {
        if (!subscription) return;
        setSaving(true); setErreur('');
        try {
            const passagesPourSauvegarde = Object.entries(periodes).map(([num, periode_label]) => ({
                occurrence_number: Number(num),
                periode_label,
                catalog_item_ids: Object.entries(quantities[Number(num)] || {}).filter(([, q]) => q > 0).map(([id]) => id),
            }));
            const res: any = await moduleApi.savePrestationDossierFormulaPassages(dossierId, estVirtuelle
                ? { formula_id: subscription.formula_id, passages: passagesPourSauvegarde }
                : { formula_subscription_id: subscription.id, passages: passagesPourSauvegarde });
            if (res?.error) { setErreur(res.error); return; }
            // Une ligne de devis PAR PASSAGE (jamais agrégée) — le composeur groupe l'affichage par
            // passage_number, et la réduction/gratuité de formule s'applique par le vrai numéro de passage.
            const items: DevisItemDraft[] = [];
            for (const [num, parPassage] of Object.entries(quantities)) {
                const occurrenceNumber = Number(num);
                const periode = periodes[occurrenceNumber] || '';
                for (const [catalogItemId, qte] of Object.entries(parPassage)) {
                    if (!qte || qte <= 0) continue;
                    items.push({
                        catalog_item_id: catalogItemId, quantity: qte, manual_discount_percent: 0, offered_quantity: 0,
                        // Pas de repli "Passage N" ici : le composeur préfixe déjà "Passage N" dans son
                        // en-tête de groupe — un repli aurait affiché "Passage 1 — Passage 1" en double.
                        passage_number: occurrenceNumber, passage_label: periode || undefined,
                    });
                }
            }
            onDone({ items, formulaSubscriptionId: estVirtuelle ? subscription.formula_id : subscription.id });
        } catch (e: any) {
            setErreur(e?.message || "Erreur lors de l'enregistrement du planning.");
        } finally { setSaving(false); }
    };

    const champ = 'w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500';

    if (loading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-content-muted" /></div>;

    // Contact Particulier, ou Professionnel sans formule active : rien à planifier, devis classique direct.
    if (contactType !== 'PROFESSIONNEL' || subscriptions.length === 0) {
        return (
            <div className="space-y-3">
                <p className="text-sm font-bold text-content flex items-center gap-2"><Repeat size={16} className="opacity-40" /> Devis à établir</p>
                <p className="text-xs text-content-muted">Composez le devis directement ici, avec les prestations du catalogue.</p>
                <button onClick={onSkip} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold">
                    <ArrowRight size={14} /> Continuer
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div>
                <p className="text-sm font-bold text-content flex items-center gap-2"><Repeat size={16} /> Formule et planning des passages</p>
                <p className="text-xs text-content-muted mt-0.5">Client Professionnel avec formule active — précisez ce qui est prévu à chaque passage de l'année pour composer le devis annuel.</p>
            </div>

            <div>
                <label className="text-xs font-bold text-content-muted">Formule pour ce dossier</label>
                <select className={champ} value={selectedSubId} onChange={(e) => setSelectedSubId(e.target.value)}>
                    <option value="">Aucune formule — devis pour une seule intervention</option>
                    {subscriptions.map((s) => (
                        <option key={s.id || s.formula_id} value={s.id || s.formula_id}>
                            {s.formula_name} — {Number(s.passages_libres) ? (s.id ? `${s.occurrences_used} passage${s.occurrences_used > 1 ? 's' : ''} réalisé${s.occurrences_used > 1 ? 's' : ''} (libres)` : 'passages libres, pas encore signée') : (s.id ? `${s.occurrences_used}/${s.occurrences_total} utilisées` : `${s.occurrences_total} passages/an, pas encore signée`)}
                        </option>
                    ))}
                </select>
            </div>

            {!selectedSubId ? (
                <button onClick={onSkip} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold">
                    <ArrowRight size={14} /> Continuer sans formule
                </button>
            ) : subscription && (
                <>
                    <div className="space-y-3">
                        {Object.keys(periodes).map((numStr) => {
                            const num = Number(numStr);
                            return (
                                <div key={num} className="p-3 rounded-xl bg-surface-soft border border-border space-y-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-black text-content-muted uppercase tracking-widest shrink-0">Passage {num}</span>
                                        <input className={`${champ} py-1.5`} placeholder="Période (ex: Mars, T2...)" value={periodes[num] || ''}
                                            onChange={(e) => setPeriodes((prev) => ({ ...prev, [num]: e.target.value }))} />
                                        {libres && Object.keys(periodes).length > 1 && (
                                            <button type="button" onClick={() => retirerPassage(num)} className="text-[11px] font-bold text-content-muted hover:text-red-500 shrink-0">Retirer</button>
                                        )}
                                    </div>
                                    <div className="space-y-1.5">
                                        {subscription.formula_items.map((it) => (
                                            <div key={it.catalog_item_id} className="flex items-center gap-2">
                                                <span className={`flex-1 text-xs ${it.required ? 'font-bold text-content' : 'text-content-muted'}`}>
                                                    {it.label}{it.required ? ' (incluse)' : ''}
                                                </span>
                                                <input type="number" min={0} disabled={it.required}
                                                    value={quantities[num]?.[it.catalog_item_id] ?? 0}
                                                    onChange={(e) => changerQuantite(num, it.catalog_item_id, Number(e.target.value))}
                                                    className="w-16 bg-surface border border-border rounded-md px-2 py-1 text-xs text-content disabled:opacity-60" />
                                            </div>
                                        ))}
                                    </div>
                                    {renderSeuils(num)}
                                </div>
                            );
                        })}
                    </div>

                    {libres && (
                        <div className="space-y-2">
                            <button type="button" onClick={ajouterPassage} className="text-xs font-bold text-brand-600 hover:underline">+ Ajouter un passage</button>
                            {seuilAnnuel > 0 && (
                                <div className="p-3 rounded-xl bg-surface-soft border border-border">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">Minimum annuel de la formule</p>
                                    <p className={`text-xs font-bold flex items-center gap-1.5 ${brutAnnuel >= seuilAnnuel ? 'text-green-500' : 'text-amber-500'}`}>
                                        {brutAnnuel >= seuilAnnuel ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />} {brutAnnuel.toFixed(2).replace('.', ',')} € / {seuilAnnuel.toFixed(2).replace('.', ',')} € TTC
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    <p className="text-[11px] text-content-muted italic">Toute prestation demandée en cours d'année en dehors de ce planning sera facturée en supplément — le devis le précisera au client.</p>

                    {erreur && <p className="text-sm text-red-500 font-semibold">{erreur}</p>}

                    <button onClick={suivant} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} Suivant — composer le devis annuel
                    </button>
                </>
            )}
        </div>
    );
}
